import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { industries } from '../data/mockData';
import {
  IndustryIcon1,
  IndustryIcon2,
  IndustryIcon3,
  IndustryIcon4,
  IndustryIcon6,
  IndustryIcon5,
  IndustryIcon7,
  IndustryIcon8,
  IndustryIcon9,
} from '../components/Icons/Icons'

const IndustryDetailPage = () => {
  const { industryId } = useParams();
  const industry = industries.find(i => i.name === industryId);

  if (!industry) {
    return (
      <div className="min-h-screen section-padding">
        <div className="container">
          <div className="text-center">
            <h1 className="display-md text-white mb-6">Industry Not Found</h1>
            <Link to="/industries">
              <Button className="btn-primary">Back to Industries</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const getIcon = (iconName) => {
    const icons = {
      IndustryIcon1,
      IndustryIcon2,
      IndustryIcon3,
      IndustryIcon4,
      IndustryIcon6,
      IndustryIcon5,
      IndustryIcon7,
      IndustryIcon8,
      IndustryIcon9,
    };
    return icons[iconName] || IndustryIcon1;
  };

  const IconComponent = getIcon(industry.icon);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-padding">
        <div className="container">
          <div className="mb-8">
            <Link to="/industries" className="inline-flex items-center text-green-400 hover:text-green-300 transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Industries
            </Link>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-green-400/10 text-green-400 mb-4">Industry Solution</Badge>
              <h1 className="display-lg text-white mb-6">{industry.name}</h1>
              <p className="body-lg mb-8">{industry.intro}</p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/demo">
                  <Button className="btn-primary glow-effect">
                    Industry Demo
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button className="btn-secondary">
                    Industry Expert Call
                  </Button>
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="h4 text-white mb-3">What the CRM needs to handle:</h4>
                  <div className="space-y-2">
                    {industry.needs.map((challenge, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="body-sm text-slate-400">{challenge}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="h4 text-white mb-3">How we set it up:</h4>
                  <div className="space-y-2">
                    {industry.setupHighlights.map((solution, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-400 mt-1 flex-shrink-0" />
                        <span className="body-sm text-slate-300">{solution}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-green-400/20 to-transparent rounded-2xl p-8">
                <div className="h-full bg-slate-800 rounded-xl flex items-center justify-center">
                  <div className="text-center">
                    <IconComponent className="h-32 w-32 text-green-400 mx-auto mb-6" />
                    <h3 className="h2 text-white mb-2">{industry.name}</h3>
                    <p className="body-md text-slate-400">Configured for your sector</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How this industry works */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="display-md text-white mb-6">How This Industry Works</h2>
            <p className="body-lg">{industry.knowledge}</p>
          </div>
        </div>
      </section>

      {/* Configuration */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="display-md text-white mb-6">How GrowSquare Is Configured</h2>
            <p className="body-lg max-w-2xl mx-auto">
              These are typical starting points. We adjust each one to your own process during onboarding.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {industry.config.map((group, index) => (
              <Card key={index} className="feature-card">
                <CardContent className="pt-6">
                  <h3 className="h3 text-white mb-4">{group.title}</h3>
                  <div className="space-y-2">
                    {group.items.map((item, i) => (
                      <div key={i} className="flex items-start space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-400 mt-1 flex-shrink-0" />
                        <span className="body-sm text-slate-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Example scenario */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="display-md text-white mb-6">Example Scenario</h2>
            <p className="body-lg max-w-2xl mx-auto">An illustrative example of how a setup might look.</p>
          </div>
          <Card className="feature-card max-w-4xl mx-auto">
            <CardContent className="pt-8">
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h3 className="h3 text-green-400 mb-2">Situation</h3>
                  <p className="body-md text-slate-300">{industry.scenario.situation}</p>
                </div>
                <div>
                  <h3 className="h3 text-green-400 mb-2">Setup</h3>
                  <p className="body-md text-slate-300">{industry.scenario.setup}</p>
                </div>
                <div>
                  <h3 className="h3 text-green-400 mb-2">Outcome</h3>
                  <p className="body-md text-slate-300">{industry.scenario.outcome}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Products used */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="display-md text-white mb-6">GrowSquare Products Used Most in This Industry</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {industry.detailsFeatures.map((feature, index) => (
              <Card key={index} className="feature-card">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 rounded-lg bg-green-400/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    </div>
                    <div>
                      <h3 className="h3 text-white mb-2">{feature.title}</h3>
                      <p className="body-sm text-slate-400">{feature.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Metrics */}
      {/* <section className="section-padding section-dark">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="display-md text-white mb-6">
              {industry.name} Performance Metrics
            </h2>
            <p className="body-lg max-w-2xl mx-auto">
              See the measurable impact GrowSquare delivers for {industry.name.toLowerCase()} companies.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {industry.detailsMetrics.map((feature, index) => (
              <div className="text-center" key={index}>
                <div className="w-16 h-16 bg-green-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <IconComponent className="h-8 w-8 text-green-400" />
                </div>
                <div className="text-4xl font-bold text-green-400 mb-2">{feature.title}</div>
                <p className="body-md text-slate-300">{feature.subtitle}</p>
                {/* <p className="body-sm text-slate-400">Average increase</p> 
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Implementation Process */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="display-md text-white mb-6">
              Implementation for {industry.name}
            </h2>
            <p className="body-lg max-w-2xl mx-auto">
              We start from your current process and adjust the setup for {industry.name.toLowerCase()}.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {industry.detailsImplementation.map((feature, index) => (
              <div className="text-center" key={index}>
                <div className="w-16 h-16 bg-green-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-400">{feature.id}</span>
                </div>
                <h3 className="h3 text-white mb-2">{feature.title}</h3>
                <p className="body-sm text-slate-400">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="display-md text-white mb-6">
              {industry.readyToTitle}
            </h2>
            <p className="body-lg mb-8 max-w-2xl mx-auto">
              {industry.readyToDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo">
                <Button className="btn-primary glow-effect">
                  Schedule Industry Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/industries">
                <Button className="btn-secondary">
                  Explore Other Industries
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
export default IndustryDetailPage;